import React, { useState, useContext, useEffect, ChangeEvent } from "react";
import { AuthContext } from "../contexts/auth";
import { ClockIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { deleteBreakTimeSlot, updateBreakTimeSlot, createBreakTimeSlot } from "../services/api_working_hours";
import { validateBreak, validateBreaks } from "../utils/timeslots";
import Swal from "sweetalert2";
import { formatTime } from "../utils/timeslots";



const EditBreaks = ({ timeSlotEditBreak, handleAddPause, creatingPause, onCancelCreatingPause, onUpdatePauseTimes }) => {
    const { token } = useContext(AuthContext);
    const [editingPauseId, setEditingPauseId] = useState(null);
    const [pauseList, setPauseList] = useState(timeSlotEditBreak.breaks.map(pause => ({
        ...pause,
        start_at: formatTime(pause?.start_at),
        end_at: formatTime(pause?.end_at)
    })));
    const [newBreakData, setNewBreakData] = useState({ start_at: '', end_at: '' });
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [originalPauseData, setOriginalPauseData] = useState(null);


    
    useEffect(() => {
        if(creatingPause){
            setEditingPauseId(null);
        }
    }, [creatingPause]);

    const handleEditClick = (pauseId) => {
        const pause = pauseList.find(p => p.id === pauseId);
        setOriginalPauseData({ ...pause });
        setEditingPauseId(pauseId);
    };


    const handleCancelEdit = () => {
        if (originalPauseData) {
            const updated = pauseList.map(p =>
                p.id === originalPauseData.id ? { ...originalPauseData } : p
            );
            setPauseList(updated);
        }
        setOriginalPauseData(null);
        setEditingPauseId(null);
        setError('');
    };

    const handleInputChange = (key, value, id) => {
        const formattedValue = value.replace(/\D/g, '');
        const limitedValue = formattedValue.slice(0, 4);
    
        let formattedTime = limitedValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");

        if (formattedValue.length <= 2 && value.length < formattedTime.length) {
            formattedTime = formattedTime.replace(":", "");
        }
        
        const hours = parseInt(formattedTime.split(":")[0], 10);
        const minutes = parseInt(formattedTime.split(":")[1] || "0", 10);

        if (hours === 24 || (hours === 23 && minutes > 59)) {
            setError("L'orario massimo consentito è 23:59.");
            return;
        }
      
        if(id) {
            setPauseList((prevData) =>
                prevData.map((pause) => {
                    if (id !== pause.id) {
                        return pause;
                    }
                    return { ...pause, [key]: formattedTime };
                })
            );
            return;
        }
      
        setNewBreakData((prevData) => ({
            ...prevData,
            [key]: formattedTime,
        }));
    };    


    const handleBreakTimeBlur = (index, field) => {
        let formattedTime = index === null ? newBreakData[field] : pauseList[index][field];
   
        if (typeof formattedTime === 'string') {
            formattedTime = formattedTime.replace(/\D/g, '');

            while (formattedTime.length < 4) {
            formattedTime += '0';
            }

            formattedTime = formattedTime.slice(0, 4);

            const hours = formattedTime.slice(0, 2);
            const minutes = formattedTime.slice(2);
            formattedTime = `${hours}:${minutes}`;

            const isValidTime = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formattedTime);

            if (index === null) {
                setNewBreakData(prev => ({
                    ...prev,
                    [field]: isValidTime ? formattedTime : '',
                }));
            } else {
                const updated = [...pauseList];
                updated[index][field] = isValidTime ? formattedTime : '';
                setPauseList(updated);
            }
        }
    };

    const handleUpdateBreakTime = async (pause) => {
        try {
            setIsSaving(true);

            if (!pause.start_at || !pause.end_at) {
                setError('Inserire entrambi gli orari');
                return;
            }

            if (pause.id === undefined) {
                console.error('ID della pausa non trovato');
                return;
            }

            try {
                validateBreaks(timeSlotEditBreak.start_at, timeSlotEditBreak.end_at, pauseList);
              }
              catch (error) {
                setError(error.message)
                return;
              }

            await updateBreakTimeSlot(token, pause.id, { start_at: pause.start_at, end_at: pause.end_at }, timeSlotEditBreak.id);

            if (onUpdatePauseTimes) {
                onUpdatePauseTimes(pauseList);
            }
            setEditingPauseId(null);
            setError('');
            Swal.fire({
                position: "center",
                icon: "success",
                title: "Salvato!",
                showConfirmButton: false,
                timer: 1500
            });
        } catch (error) {
            console.error('Errore durante l\'aggiornamento della pausa:', error);
            let errorMessage = 'Si è verificato un errore durante l\'aggiornamento della pausa. Per favore riprova.';
    
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];
    
                Object.keys(errorData).forEach((key) => {
                    const messageArray = errorData[key];
                    if (Array.isArray(messageArray)) {
                        messageArray.forEach((message) => {
                            errorMessages.push(`${key}: ${message}`);
                        });
                    }
                });
    
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }
            }
    
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
                confirmButtonText: 'OK',
            });
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const initialPauseList = timeSlotEditBreak.breaks ?? [];
        setPauseList(initialPauseList);
    }, [timeSlotEditBreak]);

    const handleDeleteBreak = async (breakId) => {
        try {
            await deleteBreakTimeSlot(token, breakId);
            const newPauseList = pauseList.filter(pause => pause.id !== breakId);
            setPauseList(newPauseList);
            
            if (onUpdatePauseTimes) {
                onUpdatePauseTimes(newPauseList);
            }

        } catch (error) {
            console.error('Errore durante l\'eliminazione della pausa:', error);
        }
    };
    
    const handleCreateBreak = async () => {
        try {
            setIsSaving(true);

            if (!newBreakData.start_at || !newBreakData.end_at) {
                setError('Inserire entrambi gli orari');
                return;
            }

            try {
                validateBreak(timeSlotEditBreak.start_at, timeSlotEditBreak.end_at, newBreakData, pauseList);
                validateBreaks(timeSlotEditBreak.start_at, timeSlotEditBreak.end_at, pauseList);
            }
            catch(error) {
                setError(error.message);
                return;
            }

            const createdBreak = await createBreakTimeSlot(token, newBreakData, timeSlotEditBreak.id);
    
            const updatedPauseList = [...pauseList, createdBreak];
            setPauseList(updatedPauseList);


            onUpdatePauseTimes?.(updatedPauseList); 
    
            if (onUpdatePauseTimes) {
                onUpdatePauseTimes(updatedPauseList);
            }
    
            setNewBreakData({ start_at: '', end_at: '' });
            onCancelCreatingPause();
            setError('');
        } catch (error) {
            console.error('Errore durante la creazione della pausa:', error);
            setError('Si è verificato un errore durante la creazione della pausa.');
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleCancelClick = () => {
        if (onCancelCreatingPause) {
            onCancelCreatingPause();
            setNewBreakData({ start_at: '', end_at: '' });
            setError('');
        }
    };

    const sortByStartTime = (a, b) => {
        const startTimeA = a.start_at;
        const startTimeB = b.start_at;
        
        if (startTimeA < startTimeB) {
          return -1;
        }
        if (startTimeA > startTimeB) {
          return 1;
        }
        return 0;
      };

    return (
        <>
            {error && (
                <p className="text-red-500 text-sm justify-center flex mt-3">{error}</p>
            )}
            <div className="mt-4">
                {creatingPause && (
                    <div className="grid md:grid-cols-6 sm:grid-cols-1 mx-4 mt-4 justify-between">
                        <div className="md:col-span-2 sm:col-span-1 flex items-center">
                            <label className="w-32 text-sm font-medium text-gray-700 flex items-center space-x-1">
                                <SvgBreak />
                                Dalle ore:
                            </label>
                            <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                                <input 
                                    type="text"
                                    placeholder='hh:mm' 
                                    value={newBreakData.start_at}
                                    onChange={(e) => handleInputChange('start_at', e.target.value)}
                                    onBlur={() => handleBreakTimeBlur(null, 'start_at')}
                                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                                />
                                <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                    <ClockIcon className='w-4 h-4' />
                                </span>
                            </div>
                        </div>
                        <div className="col-span-1 lg:col-span-2 flex items-center justify-start">
                            <label className="w-24 text-sm font-medium text-gray-700 flex items-center space-x-1 justify-end pe-2">
                                <SvgBreak />
                                Alle ore:
                            </label>
                            <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                                <input
                                    type="text"
                                    placeholder='hh:mm'
                                    value={newBreakData.end_at}
                                    onChange={(e) => handleInputChange('end_at', e.target.value)}
                                    onBlur={() => handleBreakTimeBlur(null, 'end_at')}
                                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                                />
                                <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                    <ClockIcon className='w-4 h-4' />
                                </span>
                            </div>
                        </div>
                        <div className="col-span-1 w-full sm:col-span-2 lg:col-span-1 flex items-center justify-center lg:justify-start lg:pt-0 pt-2 ">
                            <button
                                className="bg-indigo-600 text-white text-xs p-1 rounded-md hover:bg-indigo-500 focus:outline-none focus:bg-indigo-600 w-20 flex items-center justify-center mx-2 uppercase font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={handleCreateBreak}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Salvataggio...' : 'Salva'}
                            </button>
                            <button 
                                className="bg-gray-300 text-gray-700 text-xs p-1 rounded-md hover:bg-gray-400 focus:outline-none focus:bg-gray-200 w-20 flex items-center justify-center uppercase font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={handleCancelClick}
                            >
                                Annulla
                            </button>
                        </div>
                    </div>
                )}
                {pauseList
                .sort(sortByStartTime)
                .map((pause, index) => (
                    <div key={pause.id} className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3 mx-4 mt-4 w-full">
                        <div className="col-span-1 lg:col-span-2 flex items-center justify-start">
                            <label htmlFor="start-time" className="w-24 text-sm font-medium text-gray-700 flex items-center space-x-1 justify-end pe-2">
                                <SvgBreak />
                                Dalle ore:
                            </label>
                            <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                                {editingPauseId === pause.id ? (
                                    <input 
                                        type="text"
                                        placeholder='hh:mm' 
                                        value={pause.start_at}
                                        onChange={(e) => handleInputChange('start_at', e.target.value, pause.id)}
                                        onBlur={() => handleBreakTimeBlur(index, 'start_at')}
                                        className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:border-indigo-500 block w-24 shadow-md sm:text-sm" 
                                    />
                                ) : (
                                    <span className="mt-1 p-1 pl-4 rounded-md text-left block w-24 shadow-md sm:text-sm">{formatTime(pause?.start_at)}</span>
                                )}
                                <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                    <ClockIcon className='w-4 h-4' />
                                </span>
                            </div>
                        </div>
                        <div className="col-span-1 lg:col-span-2 flex items-center justify-start">
                            <label htmlFor="end-time" className="w-24 text-sm font-medium text-gray-700 flex items-center space-x-1 justify-end pe-2">
                                <SvgBreak />
                                Alle ore:
                            </label>
                            <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                                {editingPauseId === pause.id ? (
                                    <input 
                                        type="text" 
                                        placeholder='hh:mm'
                                        value={pause.end_at}
                                        onChange={(e) => handleInputChange('end_at', e.target.value, pause.id)}
                                        onBlur={() => handleBreakTimeBlur(index, 'end_at')}
                                        className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm" 
                                    />
                                ) : (
                                    <span className="mt-1 p-1 pl-4 text-left rounded-md block w-24 shadow-md sm:text-sm">{formatTime(pause?.end_at)}</span>
                                )}
                                <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                    <ClockIcon className='w-4 h-4' />
                                </span>
                            </div>
                        </div>
                        {!creatingPause && (
                            <div className="col-span-1 sm:col-span-2 lg:col-span-1 flex items-center justify-center me-8">
                                {editingPauseId === pause.id ? (
                                    <>
                                        <button 
                                            className="bg-indigo-600 text-white text-xs p-1 rounded-md hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus:bg-indigo-600 w-20 flex items-center justify-center me-1 font-semibold uppercase"
                                            onClick={() => handleUpdateBreakTime(pause)}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'Salvataggio...' : 'Salva'}
                                        </button>
                                        <button 
                                            className="bg-gray-300 text-gray-700 text-xs p-1 rounded-md hover:bg-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus:bg-gray-400 w-20 flex items-center justify-center uppercase font-semibold"
                                            onClick={handleCancelEdit}
                                        >
                                            Annulla
                                        </button>
                                    </>
                                ) : (
                                    <button 
                                        className="mr-3 ml-4 text-gray-700 font-bold px-1" title="Modifica"
                                        onClick={() => handleEditClick(pause.id)}
                                    >
                                        <PencilSquareIcon className="h-6 w-6 hover:text-indigo-500" /> 
                                    </button>
                                )}
                                <button 
                                    onClick={() => handleDeleteBreak(pause.id ?? 0)}
                                    className="text-gray-700 font-bold px-1" title="Elimina"
                                    style={{ display: editingPauseId === pause.id ? 'none' : 'block' }}
                                >
                                    <TrashIcon className="h-6 w-6 hover:text-red-500" />
                                </button>
                            </div>
                        )}
                        
                    </div>
                ))}
            </div>
        </>
        
    );
};

export default EditBreaks;


const SvgBreak = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 117 122.88" style={{ width: '19px', margin: '4px' }}>
            <g>
                <path d="M94.8,15.36c-1.18-0.34-1.86-1.56-1.53-2.74c0.34-1.18,1.56-1.86,2.74-1.53c3.15,0.9,5.79,2.39,7.82,4.59 c2.03,2.22,3.39,5.1,3.94,8.79c0.18,1.21-0.66,2.34-1.88,2.52c-1.21,0.18-2.34-0.66-2.52-1.88c-0.41-2.76-1.38-4.87-2.81-6.43 C99.13,17.12,97.17,16.04,94.8,15.36L94.8,15.36z M37.87,67.17L0.61,28.09c-0.84-0.89-0.81-2.29,0.08-3.13 c0.43-0.41,0.98-0.61,1.53-0.61v-0.01h14.53L2.27,9.86c-0.87-0.87-0.87-2.27,0-3.14c0.87-0.87,2.27-0.87,3.14,0l17.62,17.62h42.2 c0.46-6.48,3.24-12.31,7.52-16.64C77.44,2.94,83.92,0,91.08,0c7.16,0,13.64,2.94,18.34,7.7C114.1,12.45,117,19,117,26.23 c0,7.23-2.9,13.78-7.58,18.53c-4.7,4.76-11.18,7.7-18.34,7.7c-3.19,0-6.25-0.59-9.09-1.66c-2.33-0.88-4.51-2.1-6.48-3.6L56,67.18 v38.38l14.94,13.45c0.91,0.82,0.98,2.22,0.16,3.13c-0.44,0.49-1.04,0.73-1.65,0.73v0H24.77c-1.23,0-2.22-0.99-2.22-2.22 c0-0.7,0.32-1.32,0.82-1.73l14.5-13.36V67.17L37.87,67.17z M69.68,24.34h22.88c1.23,0,2.22,0.99,2.22,2.22 c0,0.66-0.29,1.26-0.75,1.67L78.64,44c1.5,1.08,3.15,1.98,4.92,2.65c2.33,0.88,4.87,1.37,7.52,1.37c5.93,0,11.3-2.43,15.18-6.36 c3.89-3.94,6.3-9.39,6.3-15.42c0-6.03-2.41-11.48-6.3-15.42c-3.88-3.93-9.25-6.36-15.18-6.36c-5.93,0-11.3,2.43-15.18,6.36 C72.42,14.33,70.13,19.06,69.68,24.34L69.68,24.34z M7.4,28.78l6.82,7.15c0.14-0.03,0.29-0.05,0.45-0.05h64.77 c0.28,0,0.54,0.05,0.79,0.14l7.08-7.25H7.4L7.4,28.78z M18.41,40.33l23.18,24.31c0.45,0.41,0.73,0.99,0.73,1.65v40.26h0 c0,0.6-0.24,1.2-0.72,1.63l-11.14,10.26h33.22l-11.29-10.16c-0.5-0.41-0.83-1.03-0.83-1.73V66.28h0.01c0-0.56,0.21-1.11,0.63-1.55 l23.83-24.41H18.41L18.41,40.33z"/>
            </g>
        </svg>
    );
};
